import { Loader } from "@/components/Loader";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  formatRiskPointChange,
  type RiskIndicator,
} from "@/revenue-intelligence/lib/risk-contracts";
import { formatCompactIsoCurrency } from "@/revenue-intelligence/lib/formatCurrency";
import { useAppStore } from "@/stores";
import { getDealOwner, getUserIdsForTeam } from "@revelate/utils";
import { getFullName } from "@revelate/utils";
import { ChevronRight, CircleAlert } from "lucide-react";
import { useState } from "react";
import { useLocation } from "wouter";
import { AtRiskDealsDataTable } from "../components/AtRiskDealsDataTable";
import { RevenueIntelligenceChatRegistration } from "../components/RevenueIntelligenceChatRegistration";
import { getChatRegistrationFromAnalysisContract } from "../lib/revenue-intelligence-analysis-contract";
import { RevenueIntelligenceChatScopeRegistration } from "../components/RevenueIntelligenceChatScopeRegistration";
import {
  AnalysisInfoTooltip,
  AnalysisNarrative,
  InsightSectionHeader,
} from "./AnalysisNarrative";
import { ForecastFilters } from "./ForecastFilters";
import {
  PipelineRiskSeverityChart,
} from "./PipelineAnalysisCharts";
import {
  getPipelineRiskConcentrationCopy,
  getPipelineRiskDealEvidenceCopy,
  getPipelineRiskDriverCopy,
  getPipelineRiskExecutiveCopy,
  getPipelineRiskManagementActionCopy,
  getPipelineRiskManagementActionSupportingCopy,
  getPipelineRiskManagementCopy,
  getPipelineRiskMovementCopy,
  getPipelineRiskPositionCopy,
} from "./pipelineRiskInsightCopy";
import {
  buildPipelineRiskL2Insights,
  getTopPipelineRiskConcentration,
  type PipelineRiskConcentration,
  type PipelineRiskManagementAction,
} from "./pipelineRiskInsights";
import {
  type PipelineRiskIndicatorRow,
  type PipelineRiskSeverityRow,
  type PipelineRiskThemeRow,
  type PipelineRiskTimingRow,
  type StateOfSalesV2PipelineRiskDeal,
  selectPipelineRiskKeyDeals,
} from "./pipelineAnalysis";
import type {
  PipelineRiskMovement,
  PipelineRiskMovementCategory,
  PipelineRiskMovementDeal,
  PipelineRiskMovementIndicatorChange,
} from "./pipelineRiskMovement";
import {
  buildStateOfSalesV2Query,
  useStateOfSalesV2Forecast,
} from "./useForecastData";
import { useStateOfSalesV2PipelineData } from "./usePipelineData";
import { buildStateOfSalesPipelineRiskChatContract } from "./stateOfSalesChatContracts";
import {
  buildStateOfSalesChatScopeActions,
  getStateOfSalesChatFiltersLabel,
} from "./stateOfSalesChatScope";

const DEALS_PER_PAGE = 8;

type DrilldownState = {
  deals: StateOfSalesV2PipelineRiskDeal[];
  description: string;
  highlightIndicator?: RiskIndicator;
  movement?: boolean;
  showForecast?: boolean;
  title: string;
};

function formatShare(value: number | null | undefined) {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("en", {
    maximumFractionDigits: 1,
    style: "percent",
  }).format(value);
}

function ThemeCard({
  companyCurrency,
  onClick,
  theme,
}: {
  companyCurrency: ReturnType<typeof useStateOfSalesV2Forecast>["companyCurrency"];
  onClick: () => void;
  theme: PipelineRiskThemeRow;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-[8px] border border-gray-200 bg-white p-4 text-left transition-colors hover:border-[#9F86C0] hover:bg-[#9F86C0]/5"
    >
      <span className="block text-sm font-medium text-[#231942]">{theme.label}</span>
      <span className="mt-2 block text-base tabular-nums text-gray-800">
        {formatCompactIsoCurrency(theme.rawValue, companyCurrency)}
      </span>
      <span className="mt-1 flex items-center justify-between text-[11px] leading-4 text-gray-500">
        <span>{theme.count} {theme.count === 1 ? "deal" : "deals"} · Deal value</span>
        <ChevronRight className="h-3.5 w-3.5" />
      </span>
    </button>
  );
}

function MovementEvidence({
  companyCurrency,
  movement,
  onSelect,
}: {
  companyCurrency: ReturnType<typeof useStateOfSalesV2Forecast>["companyCurrency"];
  movement: PipelineRiskMovement | null;
  onSelect: (
    title: string,
    description: string,
    category: PipelineRiskMovementCategory,
  ) => void;
}) {
  if (!movement?.available) {
    return (
      <Card className="border-dashed shadow-none">
        <CardContent className="flex min-h-36 items-center justify-center p-5 text-center text-sm text-gray-500">
          No deals have enough point-in-time history for a like-for-like comparison.
        </CardContent>
      </Card>
    );
  }

  const categories = [
    {
      category: movement.enteredHighRisk,
      description: "Deals that moved from Low or Medium risk into High risk.",
      label: "Entered High risk",
      showValue: true,
    },
    {
      category: movement.leftHighRisk,
      description: "Deals that moved from High risk into Medium or Low risk.",
      label: "Left High risk",
      showValue: true,
    },
    {
      category: movement.highRiskValueChanged,
      description: "Entered Deal value changed while the deals remained in High risk.",
      label: "High-risk value changed",
      showValue: true,
      signed: true,
    },
    {
      category: movement.riskIncreased,
      description: "Deals whose risk increased while remaining in the same severity bucket.",
      label: "Risk increased",
      showValue: false,
      signed: false,
    },
    {
      category: movement.riskReduced,
      description: "Deals whose risk decreased while remaining in the same severity bucket.",
      label: "Risk reduced",
      showValue: false,
      signed: false,
    },
  ];

  return (
    <Card>
      <CardHeader className="p-5 pb-3">
        <div className="flex items-center gap-1">
          <CardTitle className="text-sm">High-risk exposure movement</CardTitle>
          <AnalysisInfoTooltip
            compact
            label="30-day Pipeline Risk movement"
            text="30-day risk movement uses a like-for-like cohort: the same deals must fall within the selected period at both comparison dates. Deals entering or leaving the cohort are excluded from the movement comparison and included in the period where their Revelate Expected Close falls."
          />
        </div>
        <CardDescription className="text-xs">
          {movement.eligibleDealCount} of {movement.totalCurrentDealCount} selected-period {movement.totalCurrentDealCount === 1 ? "deal" : "deals"} have comparable history.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 p-5 pt-0">
        <div className="grid overflow-hidden rounded-[8px] border border-gray-200 sm:grid-cols-3">
          <div className="p-4">
            <p className="text-[11px] text-gray-500">30 days ago</p>
            <p className="mt-1 text-base tabular-nums text-[#231942]">
              {formatCompactIsoCurrency(
                movement.previousHighRiskDealValue,
                companyCurrency,
              )}
            </p>
          </div>
          <div className="border-y border-gray-200 p-4 sm:border-x sm:border-y-0">
            <p className="text-[11px] text-gray-500">Change</p>
            <p className={`mt-1 text-base tabular-nums ${
              movement.netHighRiskDealValueChange > 0
                ? "text-[#9F3A4A]"
                : movement.netHighRiskDealValueChange < 0
                  ? "text-[#1D819A]"
                  : "text-gray-600"
            }`}>
              {movement.netHighRiskDealValueChange > 0 ? "+" : ""}
              {formatCompactIsoCurrency(
                movement.netHighRiskDealValueChange,
                companyCurrency,
              )}
            </p>
          </div>
          <div className="p-4">
            <p className="text-[11px] text-gray-500">Current</p>
            <p className="mt-1 text-base tabular-nums text-[#231942]">
              {formatCompactIsoCurrency(
                movement.currentHighRiskDealValue,
                companyCurrency,
              )}
            </p>
          </div>
        </div>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
          {categories.map(item => (
            <button
              key={item.label}
              type="button"
              disabled={item.category.count === 0}
              className="rounded-[8px] border border-gray-200 p-3 text-left transition-colors enabled:hover:border-[#9F86C0] enabled:hover:bg-[#9F86C0]/5 disabled:cursor-default"
              onClick={() => onSelect(
                item.label,
                item.description,
                item.category,
              )}
            >
              <span className="block text-xs font-medium text-gray-700">{item.label}</span>
              <span className="mt-2 block text-sm tabular-nums text-[#231942]">
                {item.category.count === 0
                  ? "—"
                  : item.showValue
                    ? `${item.signed && item.category.dealValue > 0 ? "+" : ""}${formatCompactIsoCurrency(item.category.dealValue, companyCurrency)}`
                    : `${item.category.count} ${item.category.count === 1 ? "deal" : "deals"}`}
              </span>
              {item.category.count > 0 && item.showValue && (
                <span className="mt-1 block text-[11px] text-gray-500">
                  {item.category.count} {item.category.count === 1 ? "deal" : "deals"}
                </span>
              )}
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function ManagementActionRow({
  action,
  companyCurrency,
  onClick,
}: {
  action: PipelineRiskManagementAction;
  companyCurrency: ReturnType<typeof useStateOfSalesV2Forecast>["companyCurrency"];
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center justify-between gap-4 border-b border-gray-100 px-5 py-4 text-left last:border-0 hover:bg-gray-50/70"
    >
      <span className="min-w-0">
        <span className="block text-sm font-medium text-[#231942]">
          {getPipelineRiskManagementActionCopy(action)}
        </span>
        <span className="mt-1 block text-xs text-gray-500">
          {getPipelineRiskManagementActionSupportingCopy(action, companyCurrency)}
        </span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
    </button>
  );
}

export function RevenueIntelligenceStateOfSalesV2PipelineRiskPage() {
  const model = useStateOfSalesV2Forecast();
  const pipeline = useStateOfSalesV2PipelineData(model);
  const currentUser = useAppStore(state => state.currentUser);
  const [, setLocation] = useLocation();
  const [dealPage, setDealPage] = useState(0);
  const [drilldown, setDrilldown] = useState<DrilldownState | null>(null);
  const [selectedTheme, setSelectedTheme] = useState<PipelineRiskThemeRow | null>(null);

  if (model.isLoading || pipeline.isLoading) return <Loader />;
  if (model.error || pipeline.error || !model.forecast || !pipeline.forecastPosition) {
    return (
      <Card className="border-dashed">
        <CardContent className="flex min-h-64 flex-col items-center justify-center gap-3 text-center">
          <CircleAlert className="h-5 w-5 text-gray-400" />
          <p className="text-sm text-gray-500">Pipeline Risk data could not be loaded.</p>
          <Button variant="outline" size="sm" onClick={() => void pipeline.retry()}>Try again</Button>
        </CardContent>
      </Card>
    );
  }

  const position = pipeline.forecastPosition;
  const context = buildStateOfSalesV2Query(model.filters);

  if (position.historical) {
    return (
      <div className="space-y-5 pb-8">
        <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
          <h1 className="text-2xl font-semibold">Pipeline Risk</h1>
          <ForecastFilters model={model} />
        </div>
        <AnalysisNarrative
          label="Executive summary"
          conclusion="Historical Pipeline Risk is unavailable."
          explanation="We do not yet have the risk history needed for the selected period."
          evidence="Current open deals are not used as historical evidence."
        />
      </div>
    );
  }

  const risk = pipeline.risk;
  if (!risk) {
    return (
      <Card className="border-dashed">
        <CardContent className="p-5 text-sm text-gray-500">
          Pipeline Risk could not be calculated for this selection.
        </CardContent>
      </Card>
    );
  }

  const contextParams = new URLSearchParams(context);
  contextParams.set("dealDetailsViewSource", "state-of-sales-v2-pipeline-risk");
  const forecastHref = `/revenue-intelligence/state-of-sales-v2/forecast?${context}`;
  const healthHref = `/revenue-intelligence/state-of-sales-v2/pipeline-health?${context}`;
  const openDeal = (dealId: number | string) => setLocation(
    `/revenue-intelligence/deal/${dealId}?${contextParams.toString()}`,
  );
  const urgencyById = new Map(
    pipeline.urgencyDeals.map(deal => [String(deal.id), deal]),
  );
  const urgentDeals = risk.deals.flatMap((deal) => {
    const urgency = urgencyById.get(String(deal.id));
    if (!urgency) return [];
    return [{
      ...deal,
      urgency_bucket: urgency.urgency_bucket,
      urgency_score: urgency.urgency_score,
    }];
  });
  const keyDeals = selectPipelineRiskKeyDeals(
    risk.deals,
    pipeline.urgencyDeals,
    position.forecast,
  );
  const teamOwnerIds = model.teams.map(team => ({
    name: team.name,
    ownerIds: new Set(
      getUserIdsForTeam(model.teams, team.id, true).map(String),
    ),
  }));
  const concentrations = [
    getTopPipelineRiskConcentration(
      risk.deals,
      risk.exposedOpenDealValue,
      "sales_rep",
      "Sales rep",
      (deal) => {
        const owner = getDealOwner(deal);
        return owner ? getFullName(owner) : "Not specified";
      },
    ),
    getTopPipelineRiskConcentration(
      risk.deals,
      risk.exposedOpenDealValue,
      "team",
      "Team",
      (deal) => {
        const ownerId = getDealOwner(deal)?.id;
        if (!ownerId) return "Not specified";
        return teamOwnerIds.find(team => team.ownerIds.has(String(ownerId)))?.name
          ?? "Not specified";
      },
    ),
    getTopPipelineRiskConcentration(
      risk.deals,
      risk.exposedOpenDealValue,
      "stage",
      "Pipeline stage",
      deal => deal.stage_label ?? "Not specified",
    ),
  ].filter((row): row is PipelineRiskConcentration => row !== null)
    .toSorted((a, b) => b.share - a.share);
  const insights = buildPipelineRiskL2Insights({
    analysis: risk,
    concentrations,
    movement: pipeline.movement,
    selectedPeriodRange: pipeline.selectedPeriodRange,
    totalForecast: position.forecast,
    urgentDeals,
  });
  const executiveCopy = getPipelineRiskExecutiveCopy(insights, model.companyCurrency);
  const positionCopy = getPipelineRiskPositionCopy(insights, model.companyCurrency);
  const movementCopy = getPipelineRiskMovementCopy(insights, model.companyCurrency);
  const driverCopy = getPipelineRiskDriverCopy(insights, model.companyCurrency);
  const concentrationCopy = getPipelineRiskConcentrationCopy(
    insights,
    concentrations.map(row => ({
      dimension: row.dimension,
      label: row.label,
      share: row.share,
    })),
  );
  const managementCopy = getPipelineRiskManagementCopy(insights);
  const dealCopy = getPipelineRiskDealEvidenceCopy(keyDeals.length);
  const pipelineRiskChatContract = buildStateOfSalesPipelineRiskChatContract({
    analysis: risk,
    companyCurrency: model.companyCurrency,
    companyId: String(currentUser?.company_id ?? "unavailable"),
    concentrations,
    movement: pipeline.movement,
    pageLabel: "State of Sales · Pipeline Risk",
    periodLabel: model.period,
    filtersLabel: getStateOfSalesChatFiltersLabel(model),
    selectedPeriodRange: pipeline.selectedPeriodRange,
    totalForecast: position.forecast,
    urgencyDeals: pipeline.urgencyDeals,
  });
  const resolveChatContracts = (filters: typeof model.filters) => {
    const scoped = pipeline.resolveForFilters(filters);
    if (!scoped) return [];
    const filtersLabel = getStateOfSalesChatFiltersLabel({
      ...model,
      selectedOwners: filters.owners,
      selectedPipelines: filters.pipelines,
      selectedTeams: filters.teams,
    });
    const contract = buildStateOfSalesPipelineRiskChatContract({
      analysis: scoped.risk,
      companyCurrency: model.companyCurrency,
      companyId: String(currentUser?.company_id ?? "unavailable"),
      movement: scoped.movement,
      pageLabel: "State of Sales · Pipeline Risk",
      periodLabel: filters.period,
      filtersLabel,
      selectedPeriodRange: scoped.selectedPeriodRange,
      totalForecast: scoped.forecastPosition.forecast,
      urgencyDeals: scoped.urgencyDeals,
    });
    const registration = getChatRegistrationFromAnalysisContract(contract);
    return registration ? [registration] : [];
  };
  const highRisk = risk.severity.find(row => row.key === "high") ?? null;
  const safeDealPage = Math.min(
    dealPage,
    Math.max(0, Math.ceil(keyDeals.length / DEALS_PER_PAGE) - 1),
  );
  const visibleDeals = keyDeals.slice(
    safeDealPage * DEALS_PER_PAGE,
    (safeDealPage + 1) * DEALS_PER_PAGE,
  );
  const dealPageCount = Math.ceil(keyDeals.length / DEALS_PER_PAGE);
  const movementDeals = [
    ...(pipeline.movement?.enteredHighRisk.deals ?? []),
    ...(pipeline.movement?.leftHighRisk.deals ?? []),
    ...(pipeline.movement?.highRiskValueChanged.deals ?? []),
    ...(pipeline.movement?.riskIncreased.deals ?? []),
    ...(pipeline.movement?.riskReduced.deals ?? []),
  ];
  const movementDealsById = new Map(
    movementDeals.map(deal => [String(deal.id), deal]),
  );

  const openDealsDialog = (
    title: string,
    description: string,
    deals: StateOfSalesV2PipelineRiskDeal[],
    highlightIndicator?: RiskIndicator,
    options?: {
      movement?: boolean;
      showForecast?: boolean;
    },
  ) => setDrilldown({
    deals,
    description,
    highlightIndicator,
    movement: options?.movement,
    showForecast: options?.showForecast,
    title,
  });

  const openManagementAction = (action: PipelineRiskManagementAction) => {
    if (action.dealId !== undefined) {
      openDeal(action.dealId);
      return;
    }
    if (action.concentration) {
      openDealsDialog(
        action.concentration.label,
        `${action.concentration.dimension} concentration in exposed Deal value.`,
        action.concentration.deals,
      );
      return;
    }
    openDealsDialog(
      getPipelineRiskManagementActionCopy(action),
      getPipelineRiskManagementActionSupportingCopy(action, model.companyCurrency),
      action.dealIds.flatMap((dealId) => {
        const movementDeal = movementDealsById.get(String(dealId));
        if (movementDeal) return [movementDeal];
        const riskDeal = risk.deals.find(deal => String(deal.id) === String(dealId));
        return riskDeal ? [riskDeal] : [];
      }),
      undefined,
      {
        movement: action.type === "worsening_outlook"
          && action.supportingMetrics.valueBasis === "deal_value",
        showForecast: action.type === "forecast_dependency"
          || action.type === "urgent_deal"
          || action.type === "timing_dependency",
      },
    );
  };

  return (
    <div className="space-y-11 pb-8">
      <RevenueIntelligenceChatRegistration contract={pipelineRiskChatContract} />
      <RevenueIntelligenceChatScopeRegistration
        actions={buildStateOfSalesChatScopeActions(model, resolveChatContracts)}
        scopeKey="state_of_sales.filters"
      />
      <div className="space-y-5">
        <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
          <h1 className="text-2xl font-semibold">Pipeline Risk</h1>
          <ForecastFilters model={model} />
        </div>

        <AnalysisNarrative
          label="Executive summary"
          conclusion={executiveCopy.conclusion}
          explanation={executiveCopy.explanation}
          evidence={executiveCopy.evidence}
        />
      </div>

      <section aria-labelledby="pipeline-risk-position" className="space-y-5">
        <InsightSectionHeader
          id="pipeline-risk-position"
          label={positionCopy.label}
          heading={positionCopy.heading}
          supporting={positionCopy.supporting}
        />
        <div className="grid gap-3 md:grid-cols-3">
          <button
            type="button"
            className="text-left"
            disabled={!highRisk?.count}
            onClick={() => openDealsDialog(
              "Forecast at High risk",
              "Forecast contribution from open deals with a weighted Pipeline Risk score of 50 points or more.",
              highRisk?.deals ?? [],
              undefined,
              { showForecast: true },
            )}
          >
            <Card className={`h-full transition-colors ${highRisk?.count ? "hover:border-[#9F86C0]" : ""}`}>
              <CardHeader className="p-4">
                <CardDescription className="text-[11px]">Forecast at High risk</CardDescription>
                <CardTitle className="mt-1 text-lg tabular-nums">
                  {formatCompactIsoCurrency(highRisk?.forecastValue ?? 0, model.companyCurrency)}
                </CardTitle>
                <p className="text-xs text-gray-500">
                  {formatShare(insights.currentPosition.highRiskForecastShare)} of Forecast
                </p>
              </CardHeader>
            </Card>
          </button>
          <button
            type="button"
            className="text-left"
            disabled={!highRisk?.count}
            onClick={() => openDealsDialog(
              "High-risk deal value",
              "Normalized entered Deal value for open deals with a weighted Pipeline Risk score of 50 points or more.",
              highRisk?.deals ?? [],
            )}
          >
            <Card className={`h-full transition-colors ${highRisk?.count ? "hover:border-[#9F86C0]" : ""}`}>
              <CardHeader className="p-4">
                <CardDescription className="text-[11px]">High-risk deal value</CardDescription>
                <CardTitle className="mt-1 text-lg tabular-nums">
                  {formatCompactIsoCurrency(highRisk?.rawValue ?? 0, model.companyCurrency)}
                </CardTitle>
                <p className="text-xs text-gray-500">Normalized entered Deal value</p>
              </CardHeader>
            </Card>
          </button>
          <button
            type="button"
            className="text-left"
            disabled={!highRisk?.count}
            onClick={() => openDealsDialog(
              "High-risk deals",
              "Open deals expected to close in the selected period with a weighted Pipeline Risk score of 50 points or more.",
              highRisk?.deals ?? [],
            )}
          >
            <Card className={`h-full transition-colors ${highRisk?.count ? "hover:border-[#9F86C0]" : ""}`}>
              <CardHeader className="p-4">
                <CardDescription className="text-[11px]">High-risk deals</CardDescription>
                <CardTitle className="mt-1 text-lg tabular-nums">{highRisk?.count ?? 0}</CardTitle>
                <p className="text-xs text-gray-500">Open · selected period</p>
              </CardHeader>
            </Card>
          </button>
        </div>
        <Card>
          <CardHeader className="p-5 pb-0">
            <div className="flex items-center gap-1">
              <CardTitle className="text-sm">Exposure by severity</CardTitle>
              <AnalysisInfoTooltip
                compact
                label="Pipeline Risk severity"
                text="Each deal appears once. Low is below 25, Medium is 25 to below 50 and High is 50 or more on the existing weighted Pipeline Risk score."
              />
            </div>
            <CardDescription className="text-xs">
              Normalized entered deal value. Select a bar to review its deals.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-5 pt-2">
            <PipelineRiskSeverityChart
              companyCurrency={model.companyCurrency}
              data={risk.severity}
              onSelect={(row: PipelineRiskSeverityRow) => openDealsDialog(
                row.label,
                `${row.count} ${row.count === 1 ? "deal" : "deals"} in this severity bucket.`,
                row.deals,
              )}
            />
          </CardContent>
        </Card>
      </section>

      <section aria-labelledby="pipeline-risk-movement" className="space-y-5">
        <InsightSectionHeader
          id="pipeline-risk-movement"
          label={movementCopy.label}
          heading={movementCopy.heading}
          supporting={movementCopy.supporting}
        />
        <MovementEvidence
          companyCurrency={model.companyCurrency}
          movement={pipeline.movement}
          onSelect={(title, description, category) => openDealsDialog(
            title,
            description,
            category.deals,
            undefined,
            { movement: true },
          )}
        />
      </section>

      <section aria-labelledby="pipeline-risk-drivers" className="space-y-5">
        <InsightSectionHeader
          id="pipeline-risk-drivers"
          label={driverCopy.label}
          heading={driverCopy.heading}
          supporting={driverCopy.supporting}
        />
        {risk.themes.length ? (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            {risk.themes.map(theme => (
              <ThemeCard
                key={theme.riskGroup}
                companyCurrency={model.companyCurrency}
                theme={theme}
                onClick={() => setSelectedTheme(theme)}
              />
            ))}
          </div>
        ) : (
          <Card className="border-dashed shadow-none">
            <CardContent className="p-5 text-sm text-gray-500">
              No Pipeline Risk theme is present in this selection.
            </CardContent>
          </Card>
        )}
      </section>

      <section aria-labelledby="pipeline-risk-concentration" className="space-y-5">
        <InsightSectionHeader
          id="pipeline-risk-concentration"
          label={concentrationCopy.label}
          heading={concentrationCopy.heading}
          supporting={concentrationCopy.supporting}
        />
        <div className="grid gap-3 lg:grid-cols-3">
          <Card className="lg:col-span-2">
            <CardHeader className="p-5 pb-3">
              <CardTitle className="text-sm">Expected-close timing</CardTitle>
              <CardDescription className="text-xs">
                Deal value by Revelate Expected Close.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-2 p-5 pt-0 sm:grid-cols-2">
              {risk.timing.length ? risk.timing.map((row: PipelineRiskTimingRow) => (
                <button
                  key={row.key}
                  type="button"
                  className="flex items-start justify-between gap-4 rounded-[8px] border border-gray-200 p-3 text-left hover:border-[#9F86C0] hover:bg-[#9F86C0]/5"
                  onClick={() => openDealsDialog(
                    row.label,
                    "Exposed deals in this Revelate expected-close window.",
                    row.deals,
                  )}
                >
                  <span>
                    <span className="block text-xs font-medium text-gray-700">{row.label}</span>
                    <span className="mt-1 block text-[11px] text-gray-500">{row.count} {row.count === 1 ? "deal" : "deals"}</span>
                  </span>
                  <span className="text-xs tabular-nums text-[#231942]">
                    {formatCompactIsoCurrency(row.rawValue, model.companyCurrency)}
                  </span>
                </button>
              )) : (
                <p className="text-xs text-gray-500">Expected-close timing is unavailable.</p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="p-5 pb-3">
              <CardTitle className="text-sm">Organisational concentration</CardTitle>
              <CardDescription className="text-xs">
                Largest shares of exposed Deal value.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 p-5 pt-0">
              {concentrations.length ? concentrations.map(row => (
                <button
                  key={`${row.key}:${row.label}`}
                  type="button"
                  className="flex w-full items-center justify-between gap-3 rounded-[8px] border border-gray-200 p-3 text-left hover:border-[#9F86C0] hover:bg-[#9F86C0]/5"
                  onClick={() => openDealsDialog(
                    row.label,
                    `${row.dimension} concentration in exposed Deal value.`,
                    row.deals,
                  )}
                >
                  <span className="min-w-0">
                    <span className="block text-[11px] text-gray-500">{row.dimension}</span>
                    <span className="mt-0.5 block truncate text-xs font-medium text-gray-700">{row.label}</span>
                  </span>
                  <span className="shrink-0 text-right text-xs tabular-nums text-[#231942]">
                    <span className="block">{formatShare(row.share)}</span>
                    <span className="mt-0.5 block text-[11px] text-gray-500">
                      {formatCompactIsoCurrency(row.value, model.companyCurrency)}
                    </span>
                  </span>
                </button>
              )) : (
                <p className="text-xs text-gray-500">No comparable concentration is available.</p>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      <section aria-labelledby="pipeline-risk-management" className="space-y-5">
        <InsightSectionHeader
          id="pipeline-risk-management"
          label={managementCopy.label}
          heading={managementCopy.heading}
          supporting={managementCopy.supporting}
          links={[
            { href: forecastHref, label: "Forecast" },
            { href: healthHref, label: "Pipeline Health" },
          ]}
        />
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            {insights.managementAttention.actions.length ? (
              insights.managementAttention.actions.map(action => (
                <ManagementActionRow
                  key={action.id}
                  action={action}
                  companyCurrency={model.companyCurrency}
                  onClick={() => openManagementAction(action)}
                />
              ))
            ) : (
              <p className="p-5 text-sm text-gray-500">
                No material issue requires review for this selection.
              </p>
            )}
          </CardContent>
        </Card>
      </section>

      <section aria-labelledby="pipeline-risk-deals" className="space-y-5">
        <InsightSectionHeader
          id="pipeline-risk-deals"
          label={dealCopy.label}
          heading={dealCopy.heading}
          supporting={dealCopy.supporting}
        />
        {keyDeals.length ? (
          <>
            <div className="max-h-[62vh] overflow-y-auto">
              <AtRiskDealsDataTable
                deals={visibleDeals}
                companyCurrency={model.companyCurrency}
                displayMode="key-forecast-deals"
                showTitle={false}
                showTopRisk
                showExpectedCloseDate
                showForecastImpact
                forecastValueDisplay="contribution"
                showForecastShare
                showUrgencyBadge
                topRiskDisplay="primary-reason"
                onClickDeal={openDeal}
              />
            </div>
            {dealPageCount > 1 && (
              <div className="flex items-center justify-between gap-3 text-xs text-gray-500">
                <span>Page {safeDealPage + 1} of {dealPageCount}</span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={safeDealPage === 0}
                    onClick={() => setDealPage(Math.max(0, safeDealPage - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={safeDealPage >= dealPageCount - 1}
                    onClick={() => setDealPage(Math.min(dealPageCount - 1, safeDealPage + 1))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <Card className="border-dashed shadow-none">
            <CardContent className="p-5 text-sm text-gray-500">
              No exposed open deals contribute to Forecast in this selection.
            </CardContent>
          </Card>
        )}
      </section>

      <Dialog open={selectedTheme !== null} onOpenChange={(open) => !open && setSelectedTheme(null)}>
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle>{selectedTheme?.label ?? "Risk theme"}</DialogTitle>
            <DialogDescription>
              Select an indicator to review the affected deals. Indicator populations can overlap.
            </DialogDescription>
          </DialogHeader>
          <div className="grid max-h-[65vh] gap-3 overflow-y-auto md:grid-cols-2">
            {selectedTheme?.indicators.map((indicator: PipelineRiskIndicatorRow) => (
              <button
                key={indicator.indicator}
                type="button"
                className="rounded-[8px] border border-gray-200 p-4 text-left hover:border-[#9F86C0] hover:bg-[#9F86C0]/5"
                onClick={() => {
                  setSelectedTheme(null);
                  openDealsDialog(
                    indicator.label,
                    `${indicator.count} affected ${indicator.count === 1 ? "deal" : "deals"}, ranked by this indicator.`,
                    indicator.deals,
                    indicator.indicator,
                  );
                }}
              >
                <span className="block text-sm font-medium text-[#231942]">{indicator.label}</span>
                <span className="mt-2 block text-xs tabular-nums text-gray-600">
                  {formatCompactIsoCurrency(indicator.rawValue, model.companyCurrency)} Deal value · {indicator.count} {indicator.count === 1 ? "deal" : "deals"}
                </span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={drilldown !== null} onOpenChange={(open) => !open && setDrilldown(null)}>
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle>{drilldown?.title ?? "Pipeline Risk deals"}</DialogTitle>
            <DialogDescription>{drilldown?.description}</DialogDescription>
          </DialogHeader>
          <div className="max-h-[65vh] overflow-y-auto">
            {drilldown?.movement ? (
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-white">
                  <TableRow>
                    <TableHead>Deal</TableHead>
                    <TableHead>Deal value</TableHead>
                    <TableHead>Pipeline Risk</TableHead>
                    <TableHead>Changed signals</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(drilldown.deals as PipelineRiskMovementDeal[]).map((
                    deal: PipelineRiskMovementDeal,
                  ) => (
                    <TableRow
                      key={String(deal.id)}
                      className="cursor-pointer"
                      onClick={() => openDeal(deal.id)}
                    >
                      <TableCell className="font-medium text-[#231942]">
                        {deal.name ?? "Unnamed deal"}
                      </TableCell>
                      <TableCell className="tabular-nums">
                        {formatCompactIsoCurrency(
                          Number(deal.value ?? 0),
                          model.companyCurrency,
                        )}
                      </TableCell>
                      <TableCell className="tabular-nums">
                        {formatRiskPointChange(deal.previousRisk, deal.currentRisk)}
                      </TableCell>
                      <TableCell className="max-w-sm text-xs text-gray-600">
                        {deal.changedIndicators.length
                          ? deal.changedIndicators
                            .slice(0, 3)
                            .map((
                              change: PipelineRiskMovementIndicatorChange,
                            ) => change.label)
                            .join(", ")
                          : "No signal change available"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <AtRiskDealsDataTable
                deals={drilldown?.deals ?? []}
                companyCurrency={model.companyCurrency}
                displayMode={drilldown?.showForecast ? "key-forecast-deals" : "default"}
                showTitle={false}
                showTopRisk
                highlightIndicator={drilldown?.highlightIndicator}
                showExpectedCloseDate
                showForecastImpact={drilldown?.showForecast}
                forecastValueDisplay="contribution"
                topRiskDisplay="primary-reason"
                onClickDeal={openDeal}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
